import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PenyuluhDataType, StatusLogPenyuluhDataType } from './types'
import type {
  FilterStatusPenyuluhValues,
  MutationEditPenyuluhValues,
  MutationPenyuluhStatusValues
} from './validation'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['edit-penyuluh', key] as const

const getAllStatusPenyuluh = (
  params?: FilterStatusPenyuluhValues & { url?: string }
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<StatusLogPenyuluhDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const endpoint =
        params?.url ?? `/ketenagaan/penyuluh/${params?.id_penyuluh}/status`
      const response = await api.get<
        ServiceGetAllResponse<Array<StatusLogPenyuluhDataType>>
      >(endpoint, {
        params: {
          page: params?.page,
          per_page: params?.per_page,
          mode: params?.mode
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createStatusPenyuluh = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<StatusLogPenyuluhDataType>,
  MutationPenyuluhStatusValues
> => ({
  mutationFn: async (data: MutationPenyuluhStatusValues) => {
    try {
      const formData = new FormData()

      // Add all fields to FormData
      formData.append('id_status_baru', data.status_baru)
      formData.append('tanggal', data.tanggal)
      formData.append('keterangan', data.alasan || '')
      // Add file if exists
      if (data.lampiran) {
        formData.append('lampiran', data.lampiran)
      }

      const endpointMap: Record<string, string> = {
        desa: `/kelembagaan/posluhdes/${data.id_penyuluh}/status-logs`,
        kecamatan: `/kelembagaan/bpp/${data.id_penyuluh}/status-logs`,
        kotakab: `/kelembagaan/dinas-kota/${data.id_penyuluh}/status-logs`,
        provinsi: `/kelembagaan/dinas/${data.id_penyuluh}/status-logs`
      }
      const endpoint =
        data.bpp_type && endpointMap[data.bpp_type]
          ? endpointMap[data.bpp_type]
          : `/ketenagaan/penyuluh/${data.id_penyuluh}/status`

      const response = await api.post<
        ServiceGetByIdResponse<StatusLogPenyuluhDataType>
      >(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getDetail = (
  jenis_penyuluh: string,
  id_penyuluh: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PenyuluhDataType>> => ({
  queryKey: QUERY_KEY({ type: 'detail-penyuluh', jenis_penyuluh, id_penyuluh }),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<PenyuluhDataType>>(
        `/ketenagaan/penyuluh/${jenis_penyuluh}/${id_penyuluh}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhDataType>,
  MutationEditPenyuluhValues
> => ({
  mutationFn: async (data: MutationEditPenyuluhValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<PenyuluhDataType>>(
        `/ketenagaan/penyuluh/${data.jenis_penyuluh}/${data.id_penyuluh}`,
        {
          ...data,
          id_desa: data.id_desa.value,
          base_kecamatan: undefined,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value,
          jenjang_pendidikan: data.jenjang_pendidikan?.value,
          id_subsektor: data.subsektor.value,
          kecamatan: data.base_kecamatan?.map(kecamatan => ({
            id: kecamatan.id_kecamatan.value,
            desa: kecamatan.id_desa.map(desa => desa.value)
          })),
          subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const editPenyuluh = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAllStatusPenyuluh,
  createStatusPenyuluh,
  getDetail,
  update
}
